import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const CartContext = createContext();

const GUEST_CART_KEY = 'alaxico_guest_cart';

export const CartProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  // Get guest cart from localStorage
  const getGuestCart = () => {
    try {
      const saved = localStorage.getItem(GUEST_CART_KEY);
      return saved ? JSON.parse(saved) : { items: [], total: 0 };
    } catch {
      return { items: [], total: 0 };
    }
  };

  // Save guest cart to localStorage
  const saveGuestCart = (cartData) => {
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cartData));
  };

  // Clear guest cart
  const clearGuestCart = () => {
    localStorage.removeItem(GUEST_CART_KEY);
  };

  // Calculate cart total
  const calculateTotal = (items) => {
    return items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  // Fetch cart based on auth status
  const fetchCart = useCallback(async () => {
    if (authLoading) return;

    if (user) {
      // User is logged in - fetch from server
      try {
        const response = await api.get('/cart');
        setCart(response.data);
      } catch (error) {
        console.error('Error fetching cart:', error);
        setCart({ items: [], total: 0 });
      }
    } else {
      // Guest user - use localStorage
      const guestCart = getGuestCart();
      setCart(guestCart);
    }
    setLoading(false);
  }, [user, authLoading]);

  // Merge guest cart with user cart after login
  const mergeGuestCartWithUserCart = useCallback(async () => {
    const guestCart = getGuestCart();
    if (guestCart.items.length === 0) return;

    try {
      // Add each guest cart item to user's cart
      for (const item of guestCart.items) {
        await api.post('/cart/add', {
          product_id: item.product_id,
          quantity: item.quantity,
        });
      }
      // Clear guest cart after merge
      clearGuestCart();
      // Fetch updated user cart
      const response = await api.get('/cart');
      setCart(response.data);
      toast.success('Your cart items have been saved to your account!');
    } catch (error) {
      console.error('Error merging carts:', error);
    }
  }, []);

  // Add item to cart
  const addToCart = async (product, quantity = 1) => {
    if (user) {
      // User is logged in - add to server cart
      try {
        await api.post('/cart/add', {
          product_id: product.id,
          quantity: quantity,
        });
        await fetchCart();
        toast.success(`Added ${quantity} item(s) to cart!`);
        return true;
      } catch (error) {
        toast.error('Failed to add to cart');
        return false;
      }
    } else {
      // Guest user - add to localStorage cart
      const guestCart = getGuestCart();
      const existingItemIndex = guestCart.items.findIndex(
        item => item.product_id === product.id
      );

      if (existingItemIndex >= 0) {
        // Update quantity if item exists
        guestCart.items[existingItemIndex].quantity += quantity;
      } else {
        // Add new item
        guestCart.items.push({
          product_id: product.id,
          product: {
            id: product.id,
            name: product.name,
            price: product.price,
            original_price: product.original_price,
            images: product.images,
            image: product.images?.[0] || product.image,
          },
          quantity: quantity,
          price: product.price,
        });
      }

      guestCart.total = calculateTotal(guestCart.items);
      saveGuestCart(guestCart);
      setCart(guestCart);
      toast.success(`Added ${quantity} item(s) to cart!`);
      return true;
    }
  };

  // Update item quantity
  const updateQuantity = async (productId, newQuantity) => {
    if (newQuantity < 1) return;

    if (user) {
      try {
        await api.put('/cart/update', {
          product_id: productId,
          quantity: newQuantity,
        });
        await fetchCart();
      } catch (error) {
        toast.error('Failed to update quantity');
      }
    } else {
      const guestCart = getGuestCart();
      const itemIndex = guestCart.items.findIndex(
        item => item.product_id === productId
      );
      
      if (itemIndex >= 0) {
        guestCart.items[itemIndex].quantity = newQuantity;
        guestCart.total = calculateTotal(guestCart.items);
        saveGuestCart(guestCart);
        setCart(guestCart);
      }
    }
  };

  // Remove item from cart
  const removeFromCart = async (productId) => {
    if (user) {
      try {
        await api.delete(`/cart/remove/${productId}`);
        toast.success('Item removed from cart');
        await fetchCart();
      } catch (error) {
        toast.error('Failed to remove item');
      }
    } else {
      const guestCart = getGuestCart();
      guestCart.items = guestCart.items.filter(
        item => item.product_id !== productId
      );
      guestCart.total = calculateTotal(guestCart.items);
      saveGuestCart(guestCart);
      setCart(guestCart);
      toast.success('Item removed from cart');
    }
  };

  // Clear entire cart
  const clearCart = async () => {
    if (user) {
      try {
        await api.delete('/cart/clear');
        setCart({ items: [], total: 0 });
      } catch (error) {
        console.error('Error clearing cart:', error);
      }
    } else {
      clearGuestCart();
      setCart({ items: [], total: 0 });
    }
  };

  // Get cart item count
  const getCartCount = () => {
    return cart.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  };

  // Check if cart requires login (for checkout)
  const requiresLoginForCheckout = () => {
    return !user && cart.items.length > 0;
  };

  // Initialize cart on mount and when auth changes
  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // Merge guest cart when user logs in
  useEffect(() => {
    if (user && !authLoading) {
      mergeGuestCartWithUserCart();
    }
  }, [user, authLoading, mergeGuestCartWithUserCart]);

  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        fetchCart,
        getCartCount,
        requiresLoginForCheckout,
        isGuestCart: !user,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;
